import { PlayCircleOutline } from '@mui/icons-material';
import { Box, Card, CardContent, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { ConfirmationModal } from '~/Components';
import { useLanguage, useNavigation, useProvider } from '~/Hooks';
import { useRequestUpdateIntegrationVOD } from '~/requests/videoOnDemandService/useRequestUpdateVOD';
import { Button } from '~/UIKit';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  BORDER_BLACK_LIGHT,
  RED_USC,
  SHADOW_BLACK_LIGHT,
  SHADOW_BLACK_MEDIUM,
} from '~/Constants/Colors.constants';
import { makeStyles } from '~/UIKit/Style';
import { routeReplacer } from '~/Helpers/String.helper';
import { Route } from '~/Constants/Routes.constants';
import { useRequestVOD } from '~/requests/videoOnDemandService/useRequestVOD';

export const useStyles = makeStyles(() => ({
  confirmationParagraph: {
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      color: `${RED_USC}`,
      marginRight: '5px',
      width: '20px',
    },
    marginTop: '16px',
  },
  previewContent: {
    height: 320,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const VideoOnDemandCard: React.FC = () => {
  const { t } = useLanguage();
  const { classes } = useStyles();
  const { navigate } = useNavigation();
  const [modalOpen, setModalOpen] = useState(false);
  const { currentProvider } = useProvider();

  const { vodIntegrations, refetchVOD } = useRequestVOD();
  const { updateIntegrationVod, isVodIntegrationUpdateLoading } = useRequestUpdateIntegrationVOD({
    onSuccess: async () => {
      await refetchVOD();
      setModalOpen(false);
    },
  });

  const vodIntegration = vodIntegrations?.find((vod) => vod.homeSportsClub !== undefined);
  const isVideoOnDemandDisabled = vodIntegration?.homeSportsClub === false;

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const openVodList = () => {
    if (!isVideoOnDemandDisabled) {
      navigate(routeReplacer(Route.VIDEO_ON_DEMAND, { domain: currentProvider?.domain || '' }));
    }
  };

  const deactivateVOD = () => {
    updateIntegrationVod({
      integrationId: vodIntegration?.id as string,
      homeSportsClub: false,
      tcAcceptedDate: new Date().toISOString(),
    });
  };

  const activateVOD = () => {
    updateIntegrationVod({
      integrationId: vodIntegration?.id as string,
      homeSportsClub: true,
      tcAcceptedDate: new Date().toISOString(),
    });
  };

  return (
    <>
      {vodIntegration && (
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 3,
            margin: 2,
            boxShadow: `0px 3px 1px 0px ${SHADOW_BLACK_LIGHT}, 0px 3px 8px 0px ${SHADOW_BLACK_MEDIUM}`,
            border: `1px solid ${BORDER_BLACK_LIGHT}`,
            cursor: isVideoOnDemandDisabled ? 'default' : 'pointer',
          }}
          onClick={openVodList}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', marginRight: 2 }}>
            <PlayCircleOutline fontSize="large" />
          </Box>
          <CardContent sx={{ flex: '1 0 auto' }}>
            <Typography variant="h6" component="div">
              {t('app.vod')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('vod.tile.description')}
            </Typography>
          </CardContent>
          <Button
            size="medium"
            variant="contained"
            color={isVideoOnDemandDisabled ? 'primary' : 'error'}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenModal();
            }}
          >
            {isVideoOnDemandDisabled
              ? t('vod.tile.button.activate')
              : t('vod.tile.button.deactivate')}
          </Button>
        </Card>
      )}

      <ConfirmationModal
        open={modalOpen}
        title={isVideoOnDemandDisabled ? t('vod.modal.title') : t('vod.deactivate.modal.title')}
        type="vertical"
        confirm={{
          title: isVideoOnDemandDisabled
            ? t('vod.modal.confirm')
            : t('vod.deactivate.modal.confirm'),
          action: isVideoOnDemandDisabled ? activateVOD : deactivateVOD,
          isLoading: isVodIntegrationUpdateLoading,
        }}
        cancel={{
          title: t('vod.modal.cancel'),
          action: handleCloseModal,
          disabled: isVodIntegrationUpdateLoading,
        }}
        {...(!isVodIntegrationUpdateLoading && { closeAction: handleCloseModal })}
        destructive={!isVideoOnDemandDisabled}
      >
        <div>
          <Typography>
            <Trans
              i18nKey={
                isVideoOnDemandDisabled
                  ? 'app.vod.modals.activate.description.top'
                  : 'app.vod.modals.deactivate.description.top'
              }
              values={{ name: currentProvider?.name }}
              components={[<b key="bold" />]}
            />
          </Typography>
          <Typography className={classes.confirmationParagraph} variant="body1">
            <WarningAmberIcon />
            <Trans
              i18nKey={
                isVideoOnDemandDisabled
                  ? 'app.vod.modals.activate.description.bottom'
                  : 'app.vod.modals.deactivate.description.bottom'
              }
              values={{ name: currentProvider?.name }}
            />
          </Typography>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default VideoOnDemandCard;
